/**
 * Files API services
 *
 * Upload files to R2, manage file references between entities.
 */

import apiClient from './client';

// ============================================================================
// Types
// ============================================================================

export interface FileRecord {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  r2Key: string;
  uploadedBy: string;
  uploaderName: string | null;
  createdAt: string;
  referenceId?: string;
}

export interface FileReference {
  id: string;
  fileId: string;
  entityType: string;
  entityId: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformFile = (data: Record<string, any>): FileRecord => ({
  id: data.id,
  filename: data.filename,
  originalName: data.originalName || data.original_name,
  mimeType: data.mimeType || data.mime_type,
  sizeBytes: data.sizeBytes ?? data.size_bytes ?? 0,
  r2Key: data.r2Key || data.r2_key,
  uploadedBy: data.uploadedBy || data.uploaded_by,
  uploaderName: data.uploaderName || data.uploader_name || null,
  createdAt: data.createdAt || data.created_at,
  referenceId: data.referenceId || data.reference_id,
});

// ============================================================================
// API
// ============================================================================

export const filesApi = {
  /**
   * List files referenced by an entity
   */
  list: async (entityType: string, entityId: string): Promise<FileRecord[]> => {
    const response = await apiClient.get('/v1/files', {
      params: { entity_type: entityType, entity_id: entityId },
    });
    return (response.data.items || []).map(transformFile);
  },

  /**
   * Get file metadata by ID
   */
  get: async (id: string): Promise<FileRecord> => {
    const response = await apiClient.get(`/v1/files/${id}`);
    return transformFile(response.data);
  },

  /**
   * Get download URL for a file
   */
  getDownloadUrl: (id: string): string => {
    const baseUrl = apiClient.defaults.baseURL || '';
    return `${baseUrl}/v1/files/${id}/download`;
  },

  /**
   * Delete a file (and all its references)
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/files/${id}`);
  },

  /**
   * Create a file reference
   */
  createReference: async (fileId: string, entityType: string, entityId: string): Promise<FileReference> => {
    const response = await apiClient.post(`/v1/files/${fileId}/reference`, {
      entity_type: entityType,
      entity_id: entityId,
    });
    return response.data;
  },

  /**
   * Remove a file reference (doesn't delete the file)
   */
  deleteReference: async (referenceId: string): Promise<void> => {
    await apiClient.delete(`/v1/files/references/${referenceId}`);
  },

  /**
   * Get upload URL for Uppy XHR upload
   */
  getUploadUrl: (): string => {
    const baseUrl = apiClient.defaults.baseURL || '';
    return `${baseUrl}/v1/files/upload`;
  },
};
